<template>
	<div class="chat-root">
		<!-- ─── Panneau gauche : conversations ─── -->
		<div class="chat-sidebar">
			<div class="chat-sb-header">
				<div class="chat-sb-title-row">
					<div class="chat-sb-icon">
						<v-icon size="16" color="white">mdi-chat-outline</v-icon>
					</div>
					<p class="chat-sb-title">Conversations</p>
				</div>
				<v-text-field
					v-model="convSearch"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined dense hide-details
					class="chat-sb-search"
					color="blue darken-2"
				></v-text-field>
			</div>

			<div class="chat-conv-list">
				<div v-if="convsLoading" class="chat-conv-loading">
					<v-progress-circular indeterminate size="24" color="#c62828"></v-progress-circular>
				</div>
				<div v-else-if="filteredConvs.length === 0" class="chat-conv-empty">
					<v-icon size="32" color="#d1d5db">mdi-chat-remove-outline</v-icon>
					<p>Aucune conversation</p>
				</div>
				<div
					v-else
					v-for="conv in filteredConvs"
					:key="conv._id"
					class="chat-conv-item"
					:class="{ 'chat-conv-active': selectedConv && selectedConv._id === conv._id }"
					@click="selectConv(conv)"
				>
					<div class="chat-conv-avatar" :class="{ 'chat-conv-avatar-unread': conv.unread }">
						{{ (conv.customer?.fullName || conv.clientName || '?').charAt(0).toUpperCase() }}
					</div>
					<div class="chat-conv-info">
						<p class="chat-conv-name" :class="{ 'chat-conv-name-unread': conv.unread }">{{ conv.customer?.fullName || conv.clientName || 'Client' }}</p>
						<p class="chat-conv-last" :class="{ 'chat-conv-last-unread': conv.unread }">{{ conv.lastMessage || 'Démarrer la conversation' }}</p>
					</div>
					<div class="chat-conv-meta">
						<span class="chat-conv-time">{{ formatTime(conv.updatedAt) }}</span>
						<div v-if="conv.unread" class="chat-unread-badge">{{ conv.unread > 9 ? '9+' : conv.unread }}</div>
					</div>
				</div>
			</div>
		</div>

		<!-- ─── Panneau droit : messages ─── -->
		<div class="chat-main">
			<div v-if="!selectedConv" class="chat-empty-state">
				<v-icon size="56" color="#d1d5db">mdi-chat-outline</v-icon>
				<p class="chat-empty-title">Sélectionnez une conversation</p>
				<p class="chat-empty-sub">Choisissez une conversation dans la liste à gauche pour commencer à échanger.</p>
			</div>

			<template v-else>
				<div class="chat-main-header">
					<div class="chat-conv-avatar chat-avatar-lg">
						{{ (selectedConv.customer?.fullName || selectedConv.clientName || '?').charAt(0).toUpperCase() }}
					</div>
					<div>
						<p class="chat-main-name">{{ selectedConv.customer?.fullName || selectedConv.clientName || 'Client' }}</p>
						<p class="chat-main-sub">Client</p>
					</div>
				</div>

				<v-divider></v-divider>

				<div class="chat-messages" ref="msgContainer">
					<div v-if="msgsLoading" class="chat-msgs-loading">
						<v-progress-circular indeterminate size="28" color="#c62828"></v-progress-circular>
					</div>
					<div v-else-if="messages.length === 0" class="chat-no-msgs">
						<p>Aucun message. Soyez le premier à écrire !</p>
					</div>
					<div v-else v-for="msg in messages" :key="msg._id || msg.createdAt" class="chat-msg-row" :class="{ 'chat-msg-row-self': msg.isAdmin }">
						<div class="chat-bubble" :class="msg.isAdmin ? 'chat-bubble-self' : 'chat-bubble-other'">
							<p class="chat-bubble-text">{{ msg.text || msg.content }}</p>
							<span class="chat-bubble-time">{{ formatTime(msg.createdAt) }}</span>
						</div>
					</div>
				</div>

				<div class="chat-input-area">
					<v-text-field
						v-model="newMessage"
						placeholder="Écrire un message..."
						outlined dense hide-details rounded
						class="chat-input"
						@keyup.enter="sendMsg"
						:disabled="sending"
					></v-text-field>
					<v-btn
						:disabled="!newMessage.trim() || sending"
						:loading="sending"
						depressed rounded
						class="chat-send-btn"
						@click="sendMsg"
					>
						<v-icon>mdi-send</v-icon>
					</v-btn>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import io from "socket.io-client";
import server from "../config/address";
import service from "../services";
export default {
	data: () => ({
		socket: null,
		conversations: [],
		convsLoading: false,
		convSearch: "",
		selectedConv: null,
		messages: [],
		msgsLoading: false,
		newMessage: "",
		sending: false,
	}),
	computed: {
		filteredConvs() {
			if (!this.convSearch) return this.conversations;
			const q = this.convSearch.toLowerCase();
			return this.conversations.filter((c) => {
				const name = c.customer?.fullName || c.clientName || "";
				return name.toLowerCase().includes(q);
			});
		},
	},
	created() {
		this.loadConvs();
		this.socket = io(server.url, { auth: { token: localStorage.getItem('token') || '' } });
		this.socket.on("new_message", (data) => {
			const isCurrentConv = this.selectedConv && data.conversationId === this.selectedConv._id;
			if (isCurrentConv) {
				this.messages.push(data);
				this.$nextTick(() => this.scrollToBottom());
			}
			this._applyConvUpdate(data);
		});

		// Notification partagée reçue via la room staff-chat :
		// met à jour la liste sans ajouter le message une deuxième fois.
		this.socket.on("conversation_update", (data) => {
			this._applyConvUpdate(data);
		});
	},
	beforeDestroy() {
		if (this.socket) this.socket.disconnect();
	},
	methods: {
		_applyConvUpdate(data) {
			const conv = this.conversations.find((c) => c._id === data.conversationId);
			if (!conv) return;
			conv.lastMessage = data.text || data.content;
			conv.updatedAt = data.createdAt;
			if (!data.isAdmin && !(this.selectedConv && data.conversationId === this.selectedConv._id)) {
				conv.unread = (conv.unread || 0) + 1;
			}
		},
		loadConvs() {
			this.convsLoading = true;
			service.getConversations()
				.then((r) => { this.conversations = r.data?.conversations || r.data || []; this.convsLoading = false; })
				.catch((e) => { console.log(e); this.convsLoading = false; });
		},
		selectConv(conv) {
			if (this.selectedConv) {
				this.socket.emit("leave-conversation", this.selectedConv._id);
			}
			conv.unread = 0;
			this.selectedConv = conv;
			this.messages = [];
			this.msgsLoading = true;
			this.socket.emit("join-conversation", conv._id);
			service.getMessages(conv._id)
				.then((r) => { this.messages = r.data?.messages || r.data || []; this.msgsLoading = false; this.$nextTick(() => this.scrollToBottom()); })
				.catch((e) => { console.log(e); this.msgsLoading = false; });
		},
		sendMsg() {
			const text = this.newMessage.trim();
			if (!text || !this.selectedConv) return;
			this.sending = true;
			this.newMessage = "";
			service.sendMessage(this.selectedConv._id, text)
				.then(() => {
					this.sending = false;
				})
				.catch((e) => {
					console.log(e);
					this.sending = false;
					this.newMessage = text;
				});
		},
		scrollToBottom() {
			const el = this.$refs.msgContainer;
			if (el) el.scrollTop = el.scrollHeight;
		},
		formatTime(d) {
			if (!d) return "";
			const date = new Date(d);
			const now = new Date();
			if (date.toDateString() === now.toDateString()) {
				return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
			}
			return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
		},
	},
};
</script>

<style scoped>
.chat-root { display: flex; height: calc(100vh - 110px); background: white; border-radius: 16px; overflow: hidden; box-shadow: 0 2px 14px rgba(0,0,0,0.06); font-family: Montserrat, sans-serif; }
/* Sidebar */
.chat-sidebar { width: 300px; min-width: 300px; border-right: 1px solid #f3f4f6; display: flex; flex-direction: column; background: #fafafa; }
.chat-sb-header { padding: 18px 14px 12px; border-bottom: 1px solid #f3f4f6; }
.chat-sb-title-row { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.chat-sb-icon { width: 30px; height: 30px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.chat-sb-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0; }
.chat-sb-search { font-size: 0.8rem; }
.chat-conv-list { flex: 1; overflow-y: auto; }
.chat-conv-loading, .chat-conv-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 40px 16px; color: #9ca3af; font-size: 0.8rem; text-align: center; }
.chat-conv-item { display: flex; align-items: center; gap: 10px; padding: 12px 14px; cursor: pointer; border-bottom: 1px solid #f3f4f6; transition: background 0.15s; }
.chat-conv-item:hover { background: #f3f4f6; }
.chat-conv-active { background: #fff0f0 !important; border-left: 3px solid #c62828; }
.chat-conv-avatar { width: 38px; height: 38px; border-radius: 50%; background: linear-gradient(135deg, #c62828, #1565c0); color: white; font-size: 0.82rem; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.chat-avatar-lg { width: 40px; height: 40px; font-size: 0.88rem; }
.chat-conv-info { flex: 1; min-width: 0; }
.chat-conv-name { font-size: 0.82rem; font-weight: 700; color: #111827; margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.chat-conv-last { font-size: 0.72rem; color: #9ca3af; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.chat-conv-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
.chat-conv-time { font-size: 0.65rem; color: #9ca3af; white-space: nowrap; }
.chat-unread-badge { min-width: 18px; height: 18px; border-radius: 9px; background: #c62828; color: white; font-size: 0.65rem; font-weight: 700; font-family: Montserrat, sans-serif; display: flex; align-items: center; justify-content: center; padding: 0 4px; }
.chat-conv-name-unread { color: #111827 !important; font-weight: 800 !important; }
.chat-conv-last-unread { color: #374151 !important; font-weight: 600 !important; }
.chat-conv-avatar-unread { box-shadow: 0 0 0 2px #c62828; }
/* Main panel */
.chat-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.chat-empty-state { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 40px; text-align: center; color: #9ca3af; }
.chat-empty-title { font-size: 1rem; font-weight: 700; color: #6b7280; margin: 0; }
.chat-empty-sub { font-size: 0.78rem; color: #9ca3af; margin: 0; max-width: 280px; line-height: 1.5; }
.chat-main-header { display: flex; align-items: center; gap: 12px; padding: 14px 18px; }
.chat-main-name { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; }
.chat-main-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; }
.chat-messages { flex: 1; overflow-y: auto; padding: 16px 18px; display: flex; flex-direction: column; gap: 10px; }
.chat-msgs-loading, .chat-no-msgs { display: flex; align-items: center; justify-content: center; height: 100%; color: #9ca3af; font-size: 0.8rem; }
.chat-msg-row { display: flex; }
.chat-msg-row-self { justify-content: flex-end; }
.chat-bubble { max-width: 65%; padding: 10px 14px; border-radius: 16px; }
.chat-bubble-other { background: #f3f4f6; border-bottom-left-radius: 4px; }
.chat-bubble-self { background: #c62828; border-bottom-right-radius: 4px; }
.chat-bubble-text { font-size: 0.84rem; margin: 0 0 4px; line-height: 1.4; word-break: break-word; }
.chat-bubble-other .chat-bubble-text { color: #111827; }
.chat-bubble-self .chat-bubble-text { color: white; }
.chat-bubble-time { font-size: 0.62rem; display: block; text-align: right; }
.chat-bubble-other .chat-bubble-time { color: rgba(0,0,0,0.4); }
.chat-bubble-self .chat-bubble-time { color: rgba(255,255,255,0.7); }
.chat-input-area { display: flex; align-items: center; gap: 10px; padding: 12px 18px; border-top: 1px solid #f3f4f6; }
.chat-input { flex: 1; }
.chat-send-btn { background: #c62828 !important; color: white !important; min-width: 44px !important; width: 44px !important; height: 40px !important; padding: 0 !important; }
</style>

